/* Copy da lista do catálogo de packs publicados (ADR-0400/0464/0704). Distinto de
   `packs-catalog.ts` (detalhe/tabela/filtros, usado por `pack-detail-page.tsx` e componentes de
   `components/packs-catalog/`): esta área cobre só o wrapper de página + diálogo de takedown que
   vivem em `packs-catalog-list-page.tsx`. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PacksCatalogListContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly takedownDialogTitle: (displayName: string, version: string) => string;
  readonly takedownDialogDescription: string;
  readonly takedownConfirmLabel: string;
  readonly takedownReasonLabel: string;
  readonly toastTakenDown: string;
  readonly toastTakedownError: string;
  readonly bulk: {
    readonly selectedLabel: (count: number) => string;
    readonly actionLabel: string;
    readonly clearLabel: string;
    readonly dialogTitle: (count: number) => string;
    readonly dialogDescription: string;
    readonly successToast: (count: number) => string;
    readonly partialToast: (succeeded: number, failed: number) => string;
  };
}

const PACKS_CATALOG_LIST_CONTENT: Bilingual<PacksCatalogListContent> = {
  'pt-BR': {
    pageTitle: 'Catálogo de packs',
    pageDescription:
      'Packs publicados no registry — first-party, community aprovados e org-privados.',
    loadError: 'Falha ao carregar o catálogo de packs.',
    loadMoreButton: 'Carregar mais',
    takedownDialogTitle: (displayName, version) => `Takedown de ${displayName} v${version}`,
    takedownDialogDescription:
      'Remove esta versão imediatamente de novos downloads e instalações. Diferente de retirar do catálogo, a indisponibilidade é imediata. Informe o motivo.',
    takedownConfirmLabel: 'Fazer takedown',
    takedownReasonLabel: 'Motivo',
    toastTakenDown: 'Versão removida (takedown).',
    toastTakedownError: 'Falha ao fazer takedown da versão.',
    bulk: {
      selectedLabel: (count) =>
        count === 1 ? '1 versão live selecionada' : `${count} versões live selecionadas`,
      actionLabel: 'Fazer takedown em lote',
      clearLabel: 'Limpar seleção',
      dialogTitle: (count) =>
        count === 1 ? 'Takedown de 1 versão' : `Takedown de ${count} versões`,
      dialogDescription:
        'As versões exibidas serão removidas imediatamente. O resultado será informado por item.',
      successToast: (count) =>
        count === 1 ? 'Takedown concluído em 1 versão.' : `Takedown concluído em ${count} versões.`,
      partialToast: (succeeded, failed) =>
        `${succeeded} removida(s); ${failed} falharam ou já não estavam disponíveis.`,
    },
  },
  'en-US': {
    pageTitle: 'Packs catalog',
    pageDescription:
      'Packs published to the registry — first-party, approved community, and org-private.',
    loadError: 'Failed to load the packs catalog.',
    loadMoreButton: 'Load more',
    takedownDialogTitle: (displayName, version) => `Takedown of ${displayName} v${version}`,
    takedownDialogDescription:
      'Immediately removes this version from new downloads and installations. Unlike removing it from the catalog, unavailability is immediate. Enter the reason.',
    takedownConfirmLabel: 'Take down',
    takedownReasonLabel: 'Reason',
    toastTakenDown: 'Version removed (takedown).',
    toastTakedownError: 'Failed to take down the version.',
    bulk: {
      selectedLabel: (count) =>
        count === 1 ? '1 live version selected' : `${count} live versions selected`,
      actionLabel: 'Bulk takedown',
      clearLabel: 'Clear selection',
      dialogTitle: (count) =>
        count === 1 ? 'Takedown of 1 version' : `Takedown of ${count} versions`,
      dialogDescription:
        'The displayed versions will be removed immediately. Results are reported per item.',
      successToast: (count) =>
        count === 1
          ? 'Takedown completed for 1 version.'
          : `Takedown completed for ${count} versions.`,
      partialToast: (succeeded, failed) =>
        `${succeeded} removed; ${failed} failed or were no longer available.`,
    },
  },
};

export const usePacksCatalogListContent = (): PacksCatalogListContent =>
  useBilingual(PACKS_CATALOG_LIST_CONTENT);
